// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { uniqueId } from 'lodash';
import { useTranslation } from 'react-i18next';
import { func } from 'prop-types';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useNumberUtils from 'Hooks/useNumberUtils';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import download from 'Utils/download';
import APIInformationComponent from 'Components/APIIntegrations/APIInformation';
import { API_INFORMATION, API_INFORMATION_RESET, EMAIL_REGEX, PERMISSION } from 'Constants';

const defaultValues = {
  technicalSupportEmail: '',
};

const APIInformation = memo(({ setErrors }) => {
  const [apiInformation, setApiInformation] = useState({});
  const [loading, setLoading] = useState(true);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [isResetPending, setIsResetPending] = useState(false);
  const fetcher = useFetch();
  const { formatDate } = useNumberUtils();
  const { t } = useTranslation();
  const dateNow = formatDate(new Date().toISOString().slice(0, 19).replace(/T/, ' '), { timeStyle: undefined });
  const throwError = useAsyncError();
  const { data: { email, loggedIn, isImpersonated } = {} } = useUser();
  const { can } = usePermissions();
  const isAdmin = can(PERMISSION.ACCESS_API_INTEGRATIONS);
  const analytics = useAnalytics();
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    watch,
    getValues,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const emailValue = watch('technicalSupportEmail');
  const required = t('errors.fieldBlank');

  const validators = {
    technicalSupportEmail: {
      required,
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    },
  };

  const documentationFiles = [
    {
      uploadedInformationId: uniqueId('documentationFiles_'),
      type: 'pdf',
      fileName: 'API Technical Documentation',
      updatedAt: dateNow,
      url: '/resource/NODWebShopAPIManual.pdf'
    }
  ];

  const developerTools = [
    {
      uploadedInformationId: uniqueId('developerTools_'),
      type: 'json',
      fileName: 'Postman Collection',
      updatedAt: dateNow,
      url: '/resource/postman-collections.json'
    },
    {
      uploadedInformationId: uniqueId('developerTools_'),
      type: 'json',
      fileName: 'Postman Environment Variables',
      updatedAt: dateNow,
      url: '/api-information/download-postman-environment'
    },
    {
      uploadedInformationId: uniqueId('developerTools_'),
      type: 'html',
      linkName: t('profile.openInPostman'),
      updatedAt: dateNow,
      linkUrl: 'https://www.postman.com/networkonedistribution/workspace/network-one-distribution/overview'
    },
    {
      uploadedInformationId: uniqueId('developerTools_'),
      type: 'json',
      fileName: 'PHP library',
      updatedAt: dateNow,
      url: '/resource/webshop-api-php.zip'
    },
  ];

  const getAPIInformation = () => {
    setLoading(true);

    fetcher(API_INFORMATION).then(res => {
      setApiInformation(res || {});
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (loggedIn) {
      getAPIInformation();
    }
  }, [email, loggedIn]);

  const handleDownload = url => {
    analytics.track('download_clicked');
    download(url);
  };

  const handleSubmit = async () => {
    analytics.track('submit_api_information');
    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length || emailValue === '') {
      // Stop execution if any errors
      return;
    }

    setIsRequestPending(true);
    const data = getValues();
    fetcher(API_INFORMATION, {
      method: 'PUT',
      body: JSON.stringify(data),
    }).then(() => {
      getAPIInformation();
      reset(defaultValues);
      setErrors([{ message: t('profile.apiInformationSuccess'), type: 'success' }]);
    }).catch(err => setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => {
        setIsRequestPending(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  const handleResetCredentials = async () => {
    analytics.track('reset_api_credentials');
    setIsResetPending(true);

    fetcher(API_INFORMATION_RESET, {
      method: 'POST',
    }).then(() => {
      getAPIInformation();
      setErrors([{ message: t('profile.apiCredentialsResetSuccess'), type: 'success' }]);
    }).catch(err => setErrors([{ message: err.info?.errors?.map(i => i?.message).join('\r\n') || t('errors.generic'), type: 'error' }]))
      .finally(() => {
        setIsResetPending(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  return (
    <APIInformationComponent
      apiInformation={ apiInformation }
      loading={ loading }
      handleDownload={ handleDownload }
      documentationFiles={ documentationFiles }
      developerTools={ developerTools }
      handleSubmit={ handleSubmit }
      isRequestPending={ isRequestPending }
      register={ register }
      rhfErrors={ rhfErrors }
      validators={ validators }
      isAdmin={ isAdmin }
      isImpersonated={ isImpersonated }
      handleResetCredentials={ handleResetCredentials }
      isResetPending={ isResetPending }
      nextResetDate={ apiInformation?.apiCredentialsExpireAt }
    />
  );
});

APIInformation.propTypes = {
  setErrors: func,
};

APIInformation.displayName = 'APIInformationContainer';

export default APIInformation;
